<template>
 <!--
      When the mobile menu is open, add `overflow-hidden` to the `body` element to prevent double scrollbars
  
      Menu open: "fixed inset-0 z-40 overflow-y-auto", Menu closed: ""
      -->
 <div class="fixed top-0 hidden w-24 sidebar overflow-y-auto overflow-x-hidden md:block border-r">
  <div class="w-full flex flex-col items-center gap-2 space-y-3">
   <div class="flex-shrink-0 flex items-center py-2">
    <img class="h-10 w-auto" :src="logo" alt="Workflow" />
   </div>
   <div class="flex-1 w-2/3 px-1 space-y-1">
    <router-link
     @click="$emit('openSideNavHover', item.name), $emit('closeTicketDetail')"
     v-for="(item, index) in navigation"
     :key="item.name"
     :to="item.href"
     v-show="
      item.showModule &&
      (userPermissions.includes(`${item.permissionName}.*`) ||
       userPermissions.includes(`${item.permissionName}.view-own`) ||
       userPermissions.includes(`${item.permissionName}.view`))
     "
     @mouseenter="setHovered(index, true)"
     @mouseleave="setHovered(index, false)"
     :class="[
      item.route && this.$route?.name?.includes(item.route)
       ? 'sidebar__menu--active sidebar__text--active'
       : 'sidebar__menu--hover sidebar__text--hover sidebar__text',
      'group w-full p-3 rounded-md flex flex-col items-center text-2xl font-medium',
     ]"
     :aria-current="item.current ? 'page' : undefined"
    >
     <TooltipComponent :content="$t(item.name)" :show="hoveredLinks[index]">
      <font-awesome-icon :icon="item.icon" class="icon alt" />
     </TooltipComponent>
    </router-link>
   </div>
   <div class="border-t border-opacity-60 flex-1 w-full px-1 mt-10 space-y-2">
    <router-link
     class="relative flex items-left"
     @click="$emit('openSideNavHover', item.name)"
     v-for="item in specialNavigation"
     v-show="
      item.show &&
      (userPermissions.includes(`${item.permissionName}.*`) ||
       userPermissions.includes(`${item.permissionName}.view`))
     "
     :key="item.name"
     :to="item.href"
     :class="[
      item.route && this.$route?.name?.includes(item.route)
       ? `sidebar__menu--active_special sidebar__text--active_special`
       : `sidebar__menu--hover sidebar__text--hover sidebar__text--special`,
      `group w-full pl-2 py-1 rounded flex flex-col text-xl font-semibold`,
     ]"
     :aria-current="item.current ? 'page' : undefined"
    >
     <span class="text-xs text-left ml-1">{{ $t(`${item.name}`) }}</span>
     <span
      v-if="item.route && this.$route?.name?.includes(item.route)"
      class="absolute left-1 w-0.5 rounded h-2/3 encom_primary"
     ></span>
    </router-link>
   </div>
  </div>
  <div class="sandbox_view text-xs mx-0 w-full flex-shrink-0 pt-1 px-1">
   <div class="flex-1 w-full">
    <button
     @click="$emit('openFeedback', true)"
     class="sidebar__menu--hover mb-1 sidebar__text--hover encom_pink_text group w-full p-3 rounded-md flex flex-col items-center text-2xl font-semibold"
    >
     <font-awesome-icon icon="comment-plus" class="icon alt" />

     <span class="mt-2 text-xs text-center">{{ $t("header.feedback") }}</span>
    </button>
   </div>
   <div class="border-t border-gray-300">
    <p class="font-medium sidebar__text"><span>&copy;</span> CX-Engine</p>

    <p class="font-medium sidebar__text capitalize">
     ({{ sandbox === "true" ? $t("bannerSandbox") : $t("bannerProduction") }})
    </p>
   </div>
  </div>
 </div>

 <!-- Mobile menu -->
 <TransitionRoot as="template" :show="mobileMenuOpen">
  <Dialog as="div" class="md:hidden" @close="$emit('closeSubmenu')">
   <div class="fixed inset-0 z-40 flex">
    <TransitionChild
     as="template"
     enter="transition-opacity ease-linear duration-300"
     enter-from="opacity-0"
     enter-to="opacity-100"
     leave="transition-opacity ease-linear duration-300"
     leave-from="opacity-100"
     leave-to="opacity-0"
    >
     <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
    </TransitionChild>
    <TransitionChild
     as="template"
     enter="transition ease-in-out duration-300 transform"
     enter-from="-translate-x-full"
     enter-to="translate-x-0"
     leave="transition ease-in-out duration-300 transform"
     leave-from="translate-x-0"
     leave-to="-translate-x-full"
    >
     <div class="relative max-w-xs w-full sidebar pt-5 pb-4 flex-1 flex flex-col">
      <TransitionChild
       as="template"
       enter="ease-in-out duration-300"
       enter-from="opacity-0"
       enter-to="opacity-100"
       leave="ease-in-out duration-300"
       leave-from="opacity-100"
       leave-to="opacity-0"
      >
       <div class="absolute top-1 right-0 -mr-14 p-1">
        <button
         type="button"
         class="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
         @click="$emit('closeSubmenu')"
        >
         <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
         <span class="sr-only">Close sidebar</span>
        </button>
       </div>
      </TransitionChild>
      <div class="flex-shrink-0 px-4 flex items-center">
       <img class="h-8 w-auto" :src="logo" alt="Workflow" />
      </div>
      <div class="mt-5 flex-1 h-0 px-2 overflow-y-auto">
       <nav aria-label="Sidebar" :class="`sticky top-4 divide-malachite-light`">
        <template v-for="item in navigation" :key="item.name">
         <div v-if="!item.children" v-show="item.show">
          <div class="pb-2 space-y-1">
           <router-link
            v-show="item.href"
            :to="item.href"
            :class="[
             item.href === this.$route.path
              ? `sidebar__menu--active sidebar__text`
              : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
             `group flex items-center px-2 py-2 text-sm leading-6 font-medium `,
            ]"
            :aria-current="item.current ? 'page' : undefined"
           >
            <font-awesome-icon :icon="item.icon" class="icon alt" />
            <span class="capitalize ml-2">{{ $t(`${item.name}`) }}</span>
           </router-link>
           <a
            v-show="!item.href"
            href="#"
            :class="[
             item.href === this.$route.path
              ? `sidebar__menu--active sidebar__text`
              : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
             `group flex items-center px-2 py-2 text-sm leading-6 font-medium `,
            ]"
           >
            <font-awesome-icon :icon="item.icon" class="icon alt" />
            <span class="ml-2 capitalize">{{ $t(`${item.name}`) }}</span>
           </a>
          </div>
         </div>
         <Disclosure v-show="item.show" as="div" v-else class="space-y-1 pb-2" v-slot="{ open }">
          <router-link
           :to="item.href"
           :class="[
            item.href === this.$route.path
             ? `sidebar__menu--active sidebar__text`
             : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
            ` flex items-center px-0 py-0 text-sm leading-6 font-medium `,
           ]"
           :aria-current="item.current ? 'page' : undefined"
          >
           <DisclosureButton
            :class="[
             item.href === this.$route.path
              ? `sidebar__menu--active sidebar__text`
              : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
             `group w-full flex items-center px-2 py-2 text-left text-sm leading-6 font-medium `,
            ]"
           >
            <font-awesome-icon :icon="item.icon" class="icon alt" />

            <span class="flex-1 capitalize ml-2">
             {{ $t(`${item.name}`) }}
            </span>
            <svg
             :class="[
              open ? 'text-gray-400 rotate-90' : 'text-gray-300',
              'ml-3 flex-shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150',
             ]"
             viewBox="0 0 20 20"
             aria-hidden="true"
            >
             <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
            </svg>
           </DisclosureButton>
          </router-link>

          <DisclosurePanel class="space-y-1">
           <router-link
            v-for="subItem in item.children"
            :key="subItem.name"
            :to="subItem.href"
            :class="[
             subItem.href === this.$route.path
              ? `sidebar__menu--active sidebar__text`
              : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
             `group flex items-center py-1 text-sm leading-6 font-medium `,
            ]"
           >
            <span class="px-3">
             <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
             >
              <path
               fill-rule="evenodd"
               d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
               clip-rule="evenodd"
              />
             </svg>
            </span>
            <span class="px-0 capitalize">{{ $t(`${subItem.name}`) }}</span>
           </router-link>
          </DisclosurePanel>
         </Disclosure>
        </template>
        <hr />
        <div class="pt-5 pb-5" v-show="this.showSpecialNavigation">
         <p
          class="px-3 text-xs sidebar__text font-bold uppercase tracking-wider pb-2"
          id="communities-headline"
         ></p>
         <template v-for="item in specialNavigation" :key="item.name">
          <div v-if="!item.children">
           <div class="pb-2 space-y-1">
            <router-link
             v-show="item.href"
             :to="item.href"
             :class="[
              item.href === this.$route.path
               ? `sidebar__menu--active sidebar__text`
               : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
              `group flex items-center px-2 py-2 text-sm leading-6 font-medium`,
             ]"
             :aria-current="item.current ? 'page' : undefined"
            >
             <!-- <component
                  :is="item.icon"
                  :class="[`mr-4 flex-shrink-0 h-6 w-6`]"
                  aria-hidden="true"
                /> -->
             <span class="capitalize">
              {{ $t(`${item.name}`) }}
             </span>
            </router-link>
            <a
             v-show="!item.href"
             href="#"
             :class="[
              item.href === this.$route.path
               ? `sidebar__menu--active sidebar__text`
               : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
              `group flex items-center px-2 py-2 text-sm leading-6 font-medium `,
             ]"
            >
             <!-- <component
                  :is="item.icon"
                  :class="[`mr-4 flex-shrink-0 h-6 w-6`]"
                  aria-hidden="true"
                /> -->
             <span> {{ $t(`${item.name}`) }} </span>
            </a>
           </div>
          </div>
          <Disclosure as="div" v-else class="space-y-1 pb-2" v-slot="{ open }">
           <router-link
            :to="item.href"
            :class="[
             item.href === this.$route.path
              ? `sidebar__menu--active sidebar__text`
              : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
             ` flex items-center px-0 py-0 text-sm leading-6 font-medium `,
            ]"
            :aria-current="item.current ? 'page' : undefined"
           >
            <DisclosureButton
             :class="[
              item.href === this.$route.path
               ? `sidebar__menu--active sidebar__text`
               : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
              `group w-full flex items-center px-2 py-2 text-left text-sm leading-6 font-medium `,
             ]"
            >
             <!-- <component
                  :is="item.icon"
                  :class="[`mr-4 flex-shrink-0 h-6 w-6`]"
                  aria-hidden="true"
                /> -->

             <span class="flex-1 uppercase">
              {{ $t(`${item.name}`) }}
             </span>
             <svg
              :class="[
               open ? 'text-gray-400 rotate-90' : 'text-gray-300',
               'ml-3 flex-shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150',
              ]"
              viewBox="0 0 20 20"
              aria-hidden="true"
             >
              <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
             </svg>
            </DisclosureButton>
           </router-link>

           <DisclosurePanel class="space-y-1">
            <router-link
             v-for="subItem in item.children"
             :key="subItem.name"
             :to="subItem.href"
             :class="[
              subItem.href === this.$route.path
               ? `sidebar__menu--active sidebar__text`
               : `sidebar__menu--hover sidebar__text--hover sidebar__text`,
              `group flex items-center py-1 text-sm leading-6 font-medium `,
             ]"
            >
             <span class="px-3">
              <svg
               xmlns="http://www.w3.org/2000/svg"
               class="h-5 w-5"
               viewBox="0 0 20 20"
               fill="currentColor"
              >
               <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
               />
              </svg>
             </span>
             <span class="px-0 capitalize">{{ $t(`${subItem.name}`) }}</span>
            </router-link>
           </DisclosurePanel>
          </Disclosure>
         </template>
        </div>
        <div class="pt-0">
         <div class="mt-0 space-y-2" aria-labelledby="communities-headline">
          <a
           v-for="item in secondaryNavigation"
           :key="item.name"
           :href="item.href"
           :class="[
            `sidebar__menu--active_text sidebar__menu--hover group flex items-center px-2 py-2 text-sm leading-6 font-medium  sidebar__text--hover`,
           ]"
          >
           {{ $t(`${item.name}`) }}
          </a>
         </div>
        </div>
       </nav>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse px-2">
       <button
        v-if="adminToken"
        @click="leaveImpersonation()"
        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 encom_closed_ticket_button font-medium text-white focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
       >
        {{ $t("header.leaveImpersonate") }}
       </button>
       <a
        @click="signOut()"
        href="#"
        class="mt-1 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mr-3 sm:w-auto sm:text-sm"
       >
        {{ $t("header.logout") }}
       </a>
      </div>
     </div>
    </TransitionChild>

    <div class="flex-shrink-0 w-14" aria-hidden="true">
     <!-- Dummy element to force sidebar to shrink to fit close icon -->
    </div>
   </div>
  </Dialog>
 </TransitionRoot>
</template>

<script>
import { HomeIcon, ClockIcon, MailIcon, XIcon } from "@heroicons/vue/outline";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import {
 Dialog,
 DialogOverlay,
 Menu,
 MenuButton,
 MenuItem,
 MenuItems,
 TransitionChild,
 TransitionRoot,
} from "@headlessui/vue";
import TooltipComponent from "./TooltipComponent.vue";

const sandbox = localStorage.getItem("sandbox");
const logo = localStorage.getItem("logo");
const adminToken = localStorage.getItem("adminToken");

export default {
 name: "Sidebar",
 props: [
  "navigation",
  "secondaryNavigation",
  "specialNavigation",
  "showSpecialNavigation",
  "statisticsAvailableForProduction",
  "hasChildren",
  "openSubmenu",
  "userPermissions",
 ],
 components: {
  HomeIcon,
  ClockIcon,
  MailIcon,
  XIcon,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
  TooltipComponent,
 },
 data() {
  return {
   sandbox,
   mobileMenuOpen: false,
   hoveredLinks: [],
   logo,
   adminToken,
   version: "",
  };
 },
 created() {
  const pkg = require("../../package.json");
  this.version = pkg.version;
 },
 methods: {
  setHovered(index, isHovered) {
   this.hoveredLinks[index] = isHovered;
  },
  signOut() {
   localStorage.removeItem("token");
   localStorage.removeItem("adminToken");
   localStorage.removeItem("avatar");
   localStorage.removeItem("account");
   localStorage.removeItem("accountName");
   this.$store.dispatch("user", {});
   this.$store.dispatch("admin", false);
   this.$store.dispatch("token", "");
   this.$store.dispatch("adminToken", "");
   this.$store.dispatch("userAvatar", "");
   this.$store.dispatch("account", "");
   this.$router.push("/login");
   location.reload();
  },
  async leaveImpersonation() {
   try {
    this.$router.push("/");
    localStorage.setItem("token", this.adminToken);
    this.$store.dispatch("account", "");
    this.$store.dispatch("token", this.adminToken);
    localStorage.removeItem("account");
    impersonatePopup = false;
   } catch (error) {
    impersonatePopup = false;
    this.errorHandling(error);
   } finally {
    location.reload();
   }
  },
 },
 watch: {
  openSubmenu: function (val) {
   this.mobileMenuOpen = val;
  },
 },
};
</script>

<style>
.sandbox_view {
 position: absolute;
 bottom: 0;
}
.logout_impersonate_buttons {
 position: relative;
 bottom: 0;
}
.icon-spin {
 animation: spin 2s linear infinite;
}

@keyframes spin {
 from {
  transform: rotate(0deg);
 }
 to {
  transform: rotate(45deg);
 }
}
</style>
